/**
 * useAddCustomTags Hook
 *
 * React Query mutation for adding custom tags to a deployment.
 * Note: Tags are append-only, cannot be removed once added. Max 10 tags per deployment.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';
import { deploymentService } from '../../../api/deployment.service';
import type { DeploymentSanitized } from '../../../types/deployment.types';
import { getErrorMessage } from '../../../lib/utils/errors';

interface UseAddCustomTagsOptions {
  tenantId: string;
  applicationId: string;
  onSuccess?: (data: DeploymentSanitized) => void;
  onError?: (error: Error) => void;
}

interface AddCustomTagsVariables {
  deploymentId: string;
  customTags: string[];
}

/**
 * Hook for adding custom tags to a deployment
 */
export const useAddCustomTags = (
  options: UseAddCustomTagsOptions
): UseMutationResult<DeploymentSanitized, Error, AddCustomTagsVariables> => {
  const { tenantId, applicationId, onSuccess, onError } = options;
  const queryClient = useQueryClient();

  return useMutation<DeploymentSanitized, Error, AddCustomTagsVariables>({
    mutationFn: async ({ deploymentId, customTags }: AddCustomTagsVariables) => {
      console.log('[useAddCustomTags] Adding custom tags:', customTags, 'to deployment:', deploymentId);
      return await deploymentService.addCustomTags(tenantId, deploymentId, { customTags });
    },
    onSuccess: (data) => {
      console.log('[useAddCustomTags] Custom tags added successfully. Total tags:', data.customTags?.length);
      // Invalidate deployments list query
      queryClient.invalidateQueries({ queryKey: ['deployments', tenantId, applicationId] });
      onSuccess?.(data);
    },
    onError: (error) => {
      console.error('[useAddCustomTags] Failed to add custom tags:', getErrorMessage(error));
      onError?.(error);
    },
  });
};
